package br.com.vvrefeicoes.portal.feature.foodpass

import androidx.compose.foundation.clickable
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.ColumnScope
import androidx.compose.foundation.layout.PaddingValues
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.padding
import androidx.compose.material3.AlertDialog
import androidx.compose.material3.Button
import androidx.compose.material3.HorizontalDivider
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.OutlinedTextField
import androidx.compose.material3.Surface
import androidx.compose.material3.Text
import androidx.compose.material3.TextButton
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.rememberCoroutineScope
import androidx.compose.runtime.setValue
import androidx.compose.ui.Modifier
import androidx.compose.ui.unit.dp
import br.com.vvrefeicoes.portal.core.di.LocalAppGraph
import br.com.vvrefeicoes.portal.core.ui.components.AppFooter
import br.com.vvrefeicoes.portal.core.ui.layout.AppScrollColumn
import br.com.vvrefeicoes.portal.core.ui.theme.AppSpacing
import br.com.vvrefeicoes.portal.core.util.PeriodMode
import br.com.vvrefeicoes.portal.core.util.PeriodSelection
import br.com.vvrefeicoes.portal.core.util.SystemDateTimeProvider
import br.com.vvrefeicoes.portal.domain.model.FoodPassBilling
import br.com.vvrefeicoes.portal.domain.model.FoodPassClient
import br.com.vvrefeicoes.portal.domain.model.FoodPassConsumption
import br.com.vvrefeicoes.portal.domain.model.FoodPassStatementEntry
import br.com.vvrefeicoes.portal.domain.model.unitId
import br.com.vvrefeicoes.portal.feature.operacional.PeriodSelector
import br.com.vvrefeicoes.portal.feature.shell.LocalGlobalFilters
import kotlinx.coroutines.launch

@Composable
fun FoodPassClientsScreen() {
    val appGraph = LocalAppGraph.current
    val repository = appGraph.foodPassRepository
    val companyRepository = appGraph.companyRepository
    var clients by remember { mutableStateOf<List<FoodPassClient>>(emptyList()) }
    var selected by remember { mutableStateOf<FoodPassClient?>(null) }
    var statement by remember { mutableStateOf<List<FoodPassStatementEntry>>(emptyList()) }
    var error by remember { mutableStateOf<String?>(null) }
    var showCreateDialog by remember { mutableStateOf(false) }

    LaunchedEffect(Unit) {
        runCatching { clients = repository.loadClients() }
            .onFailure { error = it.message ?: "Nao foi possivel carregar os clientes." }
    }
    LaunchedEffect(selected?.id) {
        val client = selected ?: return@LaunchedEffect
        runCatching { statement = repository.loadStatement(client.id) }
            .onFailure { error = it.message ?: "Nao foi possivel carregar o extrato." }
    }

    FoodPassPage(title = "Food Pass / Clientes", error = error) {
        Button(onClick = { showCreateDialog = true }) { Text("Novo cliente") }
        if (selected == null) {
            Text("Selecione um cliente para consultar a carteira e o extrato.")
            clients.forEach { client ->
                Surface(
                    modifier = Modifier.fillMaxWidth().clickable { selected = client },
                    tonalElevation = 2.dp,
                    shape = MaterialTheme.shapes.medium,
                ) {
                    Row(
                        modifier = Modifier.fillMaxWidth().padding(AppSpacing.space16),
                        horizontalArrangement = Arrangement.SpaceBetween,
                    ) {
                        Column {
                            Text(client.name, style = MaterialTheme.typography.titleMedium)
                            Text(client.email.ifBlank { client.document.ifBlank { "Sem identificacao" } })
                        }
                        Text(formatCents(client.balanceCents), style = MaterialTheme.typography.titleMedium)
                    }
                }
            }
            if (clients.isEmpty()) Text("Nenhum cliente Food Pass cadastrado.")
        } else {
            val client = selected!!
            Button(onClick = { selected = null }) { Text("Voltar") }
            Text(client.name, style = MaterialTheme.typography.titleLarge)
            Text("Saldo: ${formatCents(client.balanceCents)}")
            HorizontalDivider()
            Text("Extrato", style = MaterialTheme.typography.titleMedium)
            statement.forEach { entry ->
                Row(
                    modifier = Modifier.fillMaxWidth().padding(vertical = AppSpacing.space8),
                    horizontalArrangement = Arrangement.SpaceBetween,
                ) {
                    Column(Modifier.weight(1f)) {
                        Text(entry.description)
                        Text(entry.occurredAt, style = MaterialTheme.typography.bodySmall)
                    }
                    Text("${if (entry.direction == "OUT") "-" else "+"}${formatCents(entry.amountCents)}")
                }
            }
            if (statement.isEmpty()) Text("Nenhuma movimentacao encontrada.")
        }
    }
    if (showCreateDialog) {
        CreateFoodPassClientDialog(
            onDismiss = { showCreateDialog = false },
            onCreated = { client ->
                clients = clients + client
                selected = client
                showCreateDialog = false
            },
            onError = { error = it },
            repository = repository,
        )
    }
}

@Composable
fun FoodPassConsumptionsScreen() {
    val appGraph = LocalAppGraph.current
    val repository = appGraph.foodPassRepository
    val companyRepository = appGraph.companyRepository
    val filters = LocalGlobalFilters.current
    var periodSelection by remember {
        mutableStateOf(PeriodSelection.current(SystemDateTimeProvider().currentPeriod()))
    }
    var companyId by remember { mutableStateOf<String?>(null) }
    var consumptions by remember(companyId) { mutableStateOf<List<FoodPassConsumption>>(emptyList()) }
    var error by remember { mutableStateOf<String?>(null) }

    LaunchedEffect(filters.selectedUnit?.id) {
        companyId =
            companyRepository
                .loadCompanies()
                .firstOrNull { it.unitId == filters.selectedUnit?.id }
                ?.id
    }
    LaunchedEffect(companyId, periodSelection) {
        if (companyId == null) return@LaunchedEffect
        companyId?.let { selectedCompanyId ->
            try {
                val bounds = periodBounds(periodSelection)
                consumptions = repository.loadConsumptions(selectedCompanyId, bounds.from, bounds.to)
            } catch (exception: Exception) {
                error = exception.message ?: "Nao foi possivel carregar os consumos."
            }
        }
    }

    FoodPassPage(title = "Food Pass / Consumos", error = error) {
        FoodPassPeriodSelector(
            selection = periodSelection,
            onChange = { periodSelection = it },
        )
        Text("Unidade: ${filters.selectedUnit?.name ?: "todas"}")
        Text("Periodo: ${periodSelection.label}")
        if (companyId == null) {
            Text("Selecione uma unidade no filtro global para consultar os consumos.")
        } else if (consumptions.isEmpty()) {
            Text("Nenhum consumo encontrado no periodo selecionado.")
        } else {
            consumptions.forEach { consumption ->
                Row(
                    modifier = Modifier.fillMaxWidth().padding(vertical = AppSpacing.space8),
                    horizontalArrangement = Arrangement.SpaceBetween,
                ) {
                    Column(Modifier.weight(1f)) {
                        Text("Cliente ${consumption.clientName ?: consumption.clientId}")
                        Text(consumption.consumedAt, style = MaterialTheme.typography.bodySmall)
                    }
                    Text(formatCents(consumption.amountCents))
                }
            }
        }
    }
}

@Composable
fun FoodPassBillingScreen() {
    val appGraph = LocalAppGraph.current
    val repository = appGraph.foodPassRepository
    val companyRepository = appGraph.companyRepository
    val filters = LocalGlobalFilters.current
    var periodSelection by remember {
        mutableStateOf(PeriodSelection.current(SystemDateTimeProvider().currentPeriod()))
    }
    val selectedUnitId = filters.selectedUnit?.id
    var companyId by remember { mutableStateOf<String?>(null) }
    var billing by remember(companyId) { mutableStateOf<FoodPassBilling?>(null) }
    var error by remember { mutableStateOf<String?>(null) }

    LaunchedEffect(selectedUnitId) {
        companyId = companyRepository.loadCompanies().firstOrNull { it.unitId == selectedUnitId }?.id
    }
    LaunchedEffect(companyId, periodSelection) {
        if (companyId == null) return@LaunchedEffect
        companyId?.let { selectedCompanyId ->
            val bounds = periodBounds(periodSelection)
            runCatching { billing = repository.loadBilling(selectedCompanyId, bounds.from, bounds.to) }
                .onFailure { error = it.message ?: "Nao foi possivel carregar o faturamento." }
        }
    }

    FoodPassPage(title = "Food Pass / Faturamento", error = error) {
        FoodPassPeriodSelector(
            selection = periodSelection,
            onChange = { periodSelection = it },
        )
        Text("Unidade: ${filters.selectedUnit?.name ?: "todas"}")
        if (companyId == null) {
            Text("Selecione uma unidade no filtro global para consultar o faturamento.")
        } else {
            Text("Periodo: ${periodSelection.label}")
            Text("Consumos: ${billing?.consumptionCount ?: 0}", style = MaterialTheme.typography.titleMedium)
            Text("Total: ${formatCents(billing?.totalCents ?: 0)}", style = MaterialTheme.typography.headlineSmall)
        }
    }
}

@Composable
private fun FoodPassPeriodSelector(
    selection: PeriodSelection,
    onChange: (PeriodSelection) -> Unit,
) {
    PeriodSelector(
        selection = selection,
        onMode = { onChange(selection.copy(mode = it)) },
        onMonth = { onChange(selection.copy(mode = PeriodMode.MONTH, month = it)) },
        onYear = { onChange(selection.copy(mode = PeriodMode.YEAR, year = it)) },
        onCustom = { start, end ->
            onChange(selection.copy(mode = PeriodMode.CUSTOM, start = start, end = end))
        },
    )
}

@Composable
private fun CreateFoodPassClientDialog(
    repository: br.com.vvrefeicoes.portal.domain.repository.FoodPassRepository,
    onDismiss: () -> Unit,
    onCreated: (FoodPassClient) -> Unit,
    onError: (String) -> Unit,
) {
    var name by remember { mutableStateOf("") }
    var document by remember { mutableStateOf("") }
    var email by remember { mutableStateOf("") }
    var saving by remember { mutableStateOf(false) }
    val scope = rememberCoroutineScope()

    AlertDialog(
        onDismissRequest = { if (!saving) onDismiss() },
        title = { Text("Novo cliente Food Pass") },
        text = {
            Column(verticalArrangement = Arrangement.spacedBy(AppSpacing.space8)) {
                OutlinedTextField(value = name, onValueChange = { name = it }, label = { Text("Nome") }, singleLine = true)
                OutlinedTextField(value = document, onValueChange = { document = it }, label = { Text("Documento") }, singleLine = true)
                OutlinedTextField(value = email, onValueChange = { email = it }, label = { Text("E-mail") }, singleLine = true)
            }
        },
        confirmButton = {
            Button(
                enabled = name.isNotBlank() && !saving,
                onClick = {
                    saving = true
                    scope.launch {
                        runCatching {
                            repository.createClient(name, document, email)
                        }.onSuccess(onCreated)
                            .onFailure { onError(it.message ?: "Nao foi possivel cadastrar o cliente.") }
                        saving = false
                    }
                },
            ) { Text("Cadastrar") }
        },
        dismissButton = {
            TextButton(enabled = !saving, onClick = onDismiss) { Text("Cancelar") }
        },
    )
}

@Composable
private fun FoodPassPage(
    title: String,
    error: String?,
    content: @Composable ColumnScope.() -> Unit,
) {
    AppScrollColumn(
        modifier = Modifier.fillMaxSize(),
        contentPadding = PaddingValues(AppSpacing.space24),
        footer = { AppFooter() },
    ) {
        Text(title, style = MaterialTheme.typography.headlineMedium)
        error?.let { Text(it, color = MaterialTheme.colorScheme.error) }
        Column(verticalArrangement = Arrangement.spacedBy(AppSpacing.space12), content = content)
    }
}

private fun formatCents(cents: Long): String {
    val absolute = kotlin.math.abs(cents)
    val reais = absolute / 100
    val centavos = (absolute % 100).toString().padStart(2, '0')
    return "R$ $reais,$centavos"
}

private data class FoodPassPeriodBounds(
    val from: String?,
    val to: String?,
)

private fun periodBounds(selection: br.com.vvrefeicoes.portal.core.util.PeriodSelection): FoodPassPeriodBounds =
    when (selection.mode) {
        br.com.vvrefeicoes.portal.core.util.PeriodMode.MONTH -> bounds(selection.month, selection.month.next())
        br.com.vvrefeicoes.portal.core.util.PeriodMode.YEAR ->
            bounds(
                br.com.vvrefeicoes.portal.domain.model
                    .MonthPeriod(selection.year, 1),
                br.com.vvrefeicoes.portal.domain.model
                    .MonthPeriod(selection.year + 1, 1),
            )
        br.com.vvrefeicoes.portal.core.util.PeriodMode.CUSTOM -> bounds(selection.start, selection.end.next())
        br.com.vvrefeicoes.portal.core.util.PeriodMode.TOTAL ->
            FoodPassPeriodBounds("1970-01-01T00:00:00Z", null)
    }

private fun bounds(
    start: br.com.vvrefeicoes.portal.domain.model.MonthPeriod,
    endExclusive: br.com.vvrefeicoes.portal.domain.model.MonthPeriod,
) = FoodPassPeriodBounds(monthStart(start), monthStart(endExclusive))

private fun monthStart(period: br.com.vvrefeicoes.portal.domain.model.MonthPeriod): String = "${period.year.toString().padStart(4, '0')}-${period.month.toString().padStart(2, '0')}-01T00:00:00Z"
